import React, { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { BreadcrumbContext } from '../../../../components/Breadcrumb';
import { AuthContext } from "../../../../components/FirebaseAuth";
import { CloudFunctions } from "../../../../components/FirebaseAuth/firebase";
import {Form, Input} from "../../../../components/Form";
import { useNavigate } from "react-router-dom";
import Loader from "../../../../components/Loader";
import { Card, CardContent, Alert, Select, MenuItem, FormControl, InputLabel, Stack, Button } from "@mui/material";

const AddUser = () => {
    const { t } = useTranslation();
    const mountedRef = useRef(true);
    const navigate = useNavigate();

    const { userData } = useContext(AuthContext);
    const { setBreadcrumb } = useContext(BreadcrumbContext);
    const [emailAddress, setEmailAddress] = useState({
        hasError: false,
        error: null,
        value: null
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [inSubmit, setInSubmit] = useState(false);
    const [selectedRole, setSelectedRole] = useState('user');
    const [inviteDialog, setInviteDialog] = useState(false);

    useEffect(() => {
        setBreadcrumb([
            {
                to: "/",
                text: t('common.home'),
                active: false
            },
            {
                to: "/account/"+userData.currentAccount.id+"/",
                text: userData.currentAccount.name,
                active: false
            },
            {
                to: "/account/"+userData.currentAccount.id+"/users",
                text: t('addUser.manageUsersBreadcrumb'),
                active: false
            },
            {
                to: null,
                text: t('addUser.title'),
                active: true
            }
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, setBreadcrumb, t]);

    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    },[]);

    return (
        <Card
            sx={{
                backgroundColor: (theme) => theme.custom.card,
                boxShadow: (theme) => theme.custom.cardShadow,
                border: (theme) => `1px solid ${theme.custom.border}`,
            }}
        >
            <CardContent>
            {success?(
                    <>
                        {inviteDialog?(
                            <Alert severity="success">{t('addUser.inviteSent')}</Alert>
                        ):(
                            <Alert severity="success">{t('addUser.userAdded')}</Alert>
                        )}
                        <Stack direction="row" spacing={1} mt={2}>
                            <Button variant="contained" color="primary" onClick={() => navigate("/account/"+userData.currentAccount.id+"/users")} >{t('addUser.backToUsers')}</Button>
                        </Stack>
                    </>
                ):(
                    <>
                        {error !== null &&
                            <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>
                        }
                        {inviteDialog?(
                            <>
                                <Alert severity="info">{t('addUser.inviteAsk')}</Alert>
                                <Stack direction="row" spacing={1} mt={2}>
                                    <Button variant="contained" color="primary" disabled={inSubmit} onClick={e => {
                                        e.preventDefault();
                                        setInSubmit(true);
                                        const inviteEmailToAccount = CloudFunctions.httpsCallable('inviteEmailToAccount');
                                        inviteEmailToAccount({
                                            accountId: userData.currentAccount.id,
                                            email: emailAddress.value,
                                            role: selectedRole
                                        }).then(res => {
                                            if (!mountedRef.current) return null
                                            setInSubmit(false);
                                            setSuccess(true);
                                        }).catch(err => {
                                            if (!mountedRef.current) return null
                                            setError(err.message);
                                        });
                                    }}>{inSubmit && <Loader />}
                                        {t('addUser.sendInvite')}</Button>
                                    <Button variant="contained" color="secondary" disabled={inSubmit} onClick={() => {
                                            navigate("/account/"+userData.currentAccount.id+"/users");
                                    }}>{t('addUser.no')}</Button>
                                </Stack>
                            </>
                        ):(
                            <Form handleSubmit={e => {
                                e.preventDefault();
                                setError(null);
                                setSuccess(false);
                                setInSubmit(true);
                                const addUserToAccount = CloudFunctions.httpsCallable('addUserToAccount');
                                addUserToAccount({
                                    accountId: userData.currentAccount.id,
                                    email: emailAddress.value,
                                    role: selectedRole
                                }).then(res => {
                                    if (!mountedRef.current) return null
                                    setInSubmit(false);
                                    setSuccess(true);
                                }).catch(err => {
                                    if (!mountedRef.current) return null
                                    setInSubmit(false);
                                    if(err.details && err.details.code === 'auth/user-not-found'){
                                        setInviteDialog(true);
                                        setInSubmit(false);
                                    }else{
                                        setError(err.message);
                                    }
                                });
                            }}
                            disabled={emailAddress.hasError || emailAddress.value===null || selectedRole===null || inSubmit}
                            submitBtnStyle={(selectedRole!=='remove')?"primary":"danger"}
                            inSubmit={inSubmit}
                            enableDefaultButtons={true}
                            backToUrl={"/account/"+userData.currentAccount.id+"/users"}
                            >

                                <Input label={t('addUser.emailAddress')} type="email" name="email-address" hasError={emailAddress.hasError} error={emailAddress.error} minLen={5} maxLen={50} required={true} validRegex="^[a-zA-Z0-9-_+\.]*@[a-zA-Z0-9-_\.]*\.[a-zA-Z0-9-_\.]*$" changeHandler={setEmailAddress} fullWidth />
                                <FormControl fullWidth>
                                    <InputLabel>{t('addUser.role')}</InputLabel>
                                    <Select label={t('addUser.role')} value={selectedRole} onChange={e => {
                                        setSelectedRole(e.target.value);
                                    }}>
                                        <MenuItem value="user">{t('addUser.roleUser')}</MenuItem>
                                        <MenuItem value="admin">{t('addUser.roleAdmin')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Form>
                        )}
                    </>
                )}
            </CardContent>
        </Card>

    )
}

export default AddUser;
